import React, { useEffect, useMemo, useState } from "react";
import styles from "./AddEditTransactionModal.module.css";
import { TagsAPI, TransactionsAPI, SystemAPI } from "../../../../../utils/firebase";
import { normalizeFullPath } from "../../UploadDataComponent/utils/tagUtils";
import { parseAmountValue } from "../TransactionsTableComponent/TransactionsTableComponent";
import { DescriptionTypeaheadInput, TagsMultiTypeaheadInput } from "../utils/TypeaheadInputs";

/** "yyyy-MM-dd" for <input type="date" /> */
function todayISO() { return new Date().toISOString().split("T")[0]; }

function isTransferType(val) {
  return String(val || "").trim().toLowerCase() === "transfer";
}

/** Normalize backend/legacy type strings to UI options */
function normalizeUiType(raw, fallback = "Expense") {
  const s = String(raw || "").trim().toLowerCase();
  if (s === "transfer") return "Transfer";
  if (s === "income" || /income|credit|refund/.test(s)) return "Income";
  if (s === "expense" || /expense|debit/.test(s)) return "Expense";
  return fallback;
}

function normalizeTxnTypes(systemTypes = []) {
  const out = [];
  for (const t of systemTypes) {
    const key = String(t?.key || t?.code || t?.id || t?.name || t?.label || "").trim();
    if (!key) continue;
    const label = String(t?.label || t?.name || key).trim();
    const lc = label.toLowerCase();
    const isTransfer = Boolean(t?.isTransfer ?? /transfer/.test(lc));
    let sign =
      typeof t?.sign === "number" && (t.sign === 1 || t.sign === -1 || t.sign === 0)
        ? t.sign
        : /expense|debit/.test(lc) ? -1
        : /income|credit|refund/.test(lc) ? +1
        : isTransfer ? 0 : 0;
    out.push({ key, label, isTransfer, sign });
  }
  const byKey = new Map();
  for (const t of out) byKey.set(t.key, t);
  return Array.from(byKey.values());
}

function coerceTagArrayFromComma(val) {
  return String(val || "")
    .split(",")
    .map((s) => normalizeFullPath(s.trim()))
    .filter(Boolean);
}

/**
 * Props:
 * - user
 * - mode: "add" | "edit"
 * - open
 * - initial (tx or merged transfer row)
 * - onClose()
 * - onSaved(tx or tx[])
 * - accounts: array of { id, name, currencyCode, ... }  ← from base (authoritative)
 */
export default function AddEditTransactionModal({
  user,
  mode = "add",
  open,
  initial,
  onClose,
  onSaved,
  accounts: accountsProp = [],
}) {
  const isEdit = mode === "edit";

  // accounts now come from parent
  const [accounts, setAccounts] = useState([]);
  useEffect(() => { setAccounts(Array.isArray(accountsProp) ? accountsProp : []); }, [accountsProp]);

  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [txnTypes, setTxnTypes] = useState([]);
  const [descSuggestions, setDescSuggestions] = useState([]);

  // system txn types (still fetched here)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const typesRaw = await SystemAPI.fetchTransactionTypes();
        if (alive) setTxnTypes(normalizeTxnTypes(typesRaw));
      } catch { if (alive) setTxnTypes([]); }
    })();
    return () => { alive = false; };
  }, []);

  // tag suggestions
  useEffect(() => {
    if (!open || !user?.email) return;
    const unsub = TagsAPI.subscribeUserTags({
      userEmail: user.email,
      cap: 1000,
      onData: (docs) => {
        try {
          const fulls = [];
          for (const t of docs || []) {
            const p = normalizeFullPath(t.fullPath || t.name || "");
            if (p) fulls.push(p);
          }
          fulls.sort((a, b) => a.localeCompare(b));
          setTagSuggestions(fulls);
        } catch {
          setTagSuggestions([]);
        }
      },
      onError: () => setTagSuggestions([]),
    });
    return () => { try { unsub && unsub(); } catch {} };
  }, [open, user]);

  // description suggestions (recent unique)
  useEffect(() => {
    let alive = true;
    async function loadDescs() {
      if (!open || !user?.email) return;
      try {
        const rows = await TransactionsAPI.fetchUserTransactionsRange({
          userEmail: user.email,
          cap: 500,
          latestFirst: true,
        });
        if (!alive) return;
        const seen = new Set();
        const uniq = [];
        for (const r of Array.isArray(rows) ? rows : []) {
          const d = (r.description || "").trim();
          if (!d) continue;
          const dl = d.toLowerCase();
          if (!seen.has(dl)) {
            seen.add(dl);
            uniq.push(d);
          }
          if (uniq.length >= 100) break;
        }
        setDescSuggestions(uniq);
      } catch {
        if (alive) setDescSuggestions([]);
      }
    }
    loadDescs();
    return () => { alive = false; };
  }, [open, user]);

  const typeOptions = useMemo(() => {
    const opts = [];
    const hasExpense = txnTypes.some((t) => !t.isTransfer && t.sign === -1);
    const hasIncome = txnTypes.some((t) => !t.isTransfer && t.sign === +1);
    const hasTransfer = txnTypes.some((t) => t.isTransfer);
    if (hasExpense) opts.push("Expense");
    if (hasIncome) opts.push("Income");
    if (hasTransfer) opts.push("Transfer");
    return opts.length ? opts : ["Expense", "Income", "Transfer"];
  }, [txnTypes]);

  const init = useMemo(() => {
    const d = initial || {};
    const defaultType =
      isTransferType(d.typeName || d.type) ? "Transfer" : (typeOptions[0] || "Expense");

    const isMergedTransfer =
      isTransferType(d.typeName) &&
      (d.__legs || d.transferGroupId || d.fromAccountId || d.toAccountId);

    // Normalize to UI labels so the select shows the right thing
    const normalizedType =
      isMergedTransfer || isTransferType(d.typeName || d.type)
        ? "Transfer"
        : normalizeUiType(d.typeName || d.type, defaultType);

    // Tolerate both tagPath and tagPaths when pre-filling the Tags input
    const tagsArray =
      Array.isArray(d.tagPath) ? d.tagPath
      : Array.isArray(d.tagPaths) ? d.tagPaths
      : [];

    const base = {
      typeName: normalizedType,
      description: d.description || "",
      tags: tagsArray.join(", "),
      date: d.date || todayISO(),
      memo: d.memo || "",
      status: d.status || "CLEARED",
      url: d.url || "",
      id: d.id,
    };

    if (isMergedTransfer) {
      // For merged transfer rows (projection)
      const amtAbs =
        typeof d.amountAbs === "number" ? d.amountAbs :
        typeof d.amount === "number" ? Math.abs(d.amount) :
        "";
      return {
        ...base,
        amount: amtAbs !== "" ? String(amtAbs) : "",
        accountIdSource: d.fromAccountId || "",
        accountIdDestination: d.toAccountId || "",
        accountId: "",
      };
    }

    // Non-transfer / single leg edit or add
    return {
      ...base,
      amount:
        typeof d.amount === "number" || typeof d.amount === "string"
          ? String(d.amount)
          : "",
      accountId: d.accountId || "",
      accountName: d.accountName || "",
      accountIdSource: "",
      accountIdDestination: "",
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, typeOptions.join("|")]);

  const [form, setForm] = useState(init);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setForm(init);
    setErr(null);
  }, [init, open]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // keep dependent fields clean when switching type
  useEffect(() => {
    const t = form.typeName;
    if (isTransferType(t)) {
      if (form.accountId) setForm((f) => ({ ...f, accountId: "" }));
    } else {
      if (form.accountIdSource || form.accountIdDestination) {
        setForm((f) => ({ ...f, accountIdSource: "", accountIdDestination: "" }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.typeName]);

  const validate = () => {
    const out = {};
    const amt = parseAmountValue(form.amount);
    if (!Number.isFinite(amt)) out.amount = "Enter a valid amount";
    if (!form.date) out.date = "Pick a date";

    if (isTransferType(form.typeName)) {
      if (!form.accountIdSource) out.accountIdSource = "Choose source account";
      if (!form.accountIdDestination) out.accountIdDestination = "Choose destination account";

      // 👇 ALLOW SAME SOURCE/DEST — balance adjustment is valid
      // (We keep currency guard below.)
      const src = accounts.find((a) => a.id === form.accountIdSource);
      const dst = accounts.find((a) => a.id === form.accountIdDestination);
      if (src && dst && src.currencyCode && dst.currencyCode && src.currencyCode !== dst.currencyCode) {
        out.accountIdDestination = `Currency mismatch: ${src.currencyCode} → ${dst.currencyCode}`;
      }
    } else {
      if (!form.accountId) out.accountId = "Pick an account";
    }
    return out;
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    const v = validate();
    if (Object.keys(v).length) {
      setErr({ message: Object.values(v)[0] });
      return;
    }

    setBusy(true);
    setErr(null);
    try {
      const amt = parseAmountValue(form.amount);
      const tagPath = coerceTagArrayFromComma(form.tags);

      const baseCommon = {
        description: form.description,
        tagPath,
        date: form.date,
        memo: form.memo,
        status: form.status,
        url: form.url,
      };

      if (isTransferType(form.typeName)) {
        // Single-unit transfer create/update
        const src = accounts.find((a) => a.id === form.accountIdSource);
        const dst = accounts.find((a) => a.id === form.accountIdDestination);
        const amountAbs = Math.abs(amt);

        if (isEdit && (initial?.transferGroupId || initial?.id)) {
          const transferGroupId = String(initial.transferGroupId || initial.id);
          const patch = {
            amountAbs,
            date: form.date,
            description: form.description,
            tagPath,
            fromAccountId: form.accountIdSource || undefined,
            toAccountId: form.accountIdDestination || undefined,
          };
          const saved = await TransactionsAPI.updateTransferGroup({
            user,
            transferGroupId,
            patch,
          });
          onSaved?.(saved || { transferGroupId, ...patch });
          onClose?.();
          setBusy(false);
          return;
        }

        const currencyCode = src?.currencyCode || dst?.currencyCode || undefined;
        const created = await TransactionsAPI.createTransfer({
          user,
          fromAccountId: form.accountIdSource,
          toAccountId: form.accountIdDestination,
          amountAbs,
          currencyCode,
          date: form.date,
          description: form.description,
          tagPath,
        });
        onSaved?.(created || { ...baseCommon, amountAbs, fromAccountId: form.accountIdSource, toAccountId: form.accountIdDestination });
        onClose?.();
        setBusy(false);
        return;
      }

      // Non-transfer
      const acct = accounts.find((a) => a.id === form.accountId);
      const typeSign = form.typeName === "Income" ? +1 : -1;
      const payload = {
        ...(initial || {}),
        ...baseCommon,
        id: form.id,
        typeName: form.typeName,
        amount: typeSign === -1 ? -Math.abs(amt) : Math.abs(amt),
        accountId: form.accountId,
        accountName: acct?.name || form.accountName || "",
        currencyCode: acct?.currencyCode,
        source: isEdit ? initial?.source || "manual" : "manual",
      };

      let saved;
      if (isEdit)
        saved = await TransactionsAPI.updateManualTransaction({ user, tx: payload });
      else saved = await TransactionsAPI.createManualTransaction({ user, tx: payload });

      onSaved?.(saved || payload);
      onClose?.();
    } catch (e2) {
      setErr(e2);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const transferMode = isTransferType(form.typeName);

  return (
    <div className={`modal d-block ${styles.modalBackdrop}`} role="dialog" aria-modal="true">
      <div className={`modal-dialog modal-lg modal-dialog-centered ${styles.dialog}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {isEdit ? "Edit Transaction" : "Add Transaction"}
            </h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onClose}
            />
          </div>

          <form onSubmit={onSubmit}>
            <div className="modal-body">
              {err && (
                <div className="alert alert-danger py-2">
                  {String(err?.message || err)}
                </div>
              )}

              <div className="row g-3">
                <div className="col-12 col-md-4">
                  <label className="form-label">Type</label>
                  <select
                    className="form-select"
                    value={form.typeName}
                    onChange={onChange("typeName")}
                  >
                    {typeOptions.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-8">
                  <label className="form-label">Description</label>
                  <DescriptionTypeaheadInput
                    value={form.description}
                    onChange={(v) => setForm((f) => ({ ...f, description: v }))}
                    suggestions={descSuggestions}
                    placeholder="Description"
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label">
                    Amount {transferMode ? <span className="text-secondary">(positive)</span> : null}
                  </label>
                  <input
                    className="form-control"
                    inputMode="decimal"
                    value={form.amount}
                    onChange={onChange("amount")}
                    placeholder={transferMode ? "e.g., 120.00" : "e.g., -120.00 (expense) or 120.00 (income)"}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.date}
                    onChange={onChange("date")}
                  />
                </div>

                {!transferMode ? (
                  <div className="col-12 col-md-4">
                    <label className="form-label">Account</label>
                    <select
                      className="form-select"
                      value={form.accountId}
                      onChange={onChange("accountId")}
                    >
                      <option value="">Select account…</option>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <>
                    <div className="col-12 col-md-4">
                      <label className="form-label">From (Source)</label>
                      <select
                        className="form-select"
                        value={form.accountIdSource}
                        onChange={onChange("accountIdSource")}
                      >
                        <option value="">Select…</option>
                        {accounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label">To (Destination)</label>
                      <select
                        className="form-select"
                        value={form.accountIdDestination}
                        onChange={onChange("accountIdDestination")}
                      >
                        <option value="">Select…</option>
                        {accounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                <div className="col-12 col-md-8">
                  <label className="form-label">Tags</label>
                  <TagsMultiTypeaheadInput
                    value={form.tags}
                    onChange={(v) => setForm((f) => ({ ...f, tags: v }))}
                    suggestions={tagSuggestions}
                    placeholder="Comma-separated. e.g., Services, Banking & Finance / Shared"
                    maxMatches={5}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={form.status}
                    onChange={onChange("status")}
                  >
                    <option value="CLEARED">CLEARED</option>
                    <option value="PENDING">PENDING</option>
                  </select>
                </div>

                <div className="col-12">
                  <label className="form-label">Memo</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={form.memo}
                    onChange={onChange("memo")}
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">URL</label>
                  <input
                    className="form-control"
                    value={form.url}
                    onChange={onChange("url")}
                    placeholder="(optional receipt link)"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {isEdit ? "Update Transaction" : "Add Transaction"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
